// Country content. One shared template (`CountryPage`) renders these.
// Edit copy here as the team finalises each market.

export const COUNTRIES = [
  {
    slug: "germany",
    name: "Germany",
    flag: "🇩🇪",
    tagline: "Our flagship destination",
    highlight: true,
    accent: { bg: "bg-rose-50", text: "text-rose-700", ring: "ring-rose-200" },
    hero: {
      headline: "Study in Germany — engineered for ambitious careers.",
      sub: "Public universities, low/no tuition, an 18-month post-study work visa and a Berlin office on the ground. We've placed 1,200+ students here.",
    },
    whyChoose: [
      { title: "Tuition almost free at public unis", desc: "Most public universities charge only a semester contribution (€150–300)." },
      { title: "18-month post-study work visa",      desc: "Find a job after graduation with an extended stay route to PR." },
      { title: "Strong engineering & tech industry", desc: "Siemens, BMW, SAP, Bosch, Volkswagen — the world's biggest employers." },
      { title: "Globally recognised degrees",         desc: "Bachelor's and master's accepted across the EU and beyond." },
      { title: "Berlin office support",               desc: "Real on-ground help with registration, housing & jobs after you land." },
      { title: "Easy intake calendar",                desc: "Two main intakes — Summer (Mar–Apr) and Winter (Sep–Oct)." },
    ],
    topUniversities: [
      "Technical University of Munich (TUM)",
      "SRH Hochschule Berlin",
      "GISMA Business School",
      "University of Europe for Applied Sciences",
      "IU International University",
      "Berlin School of Business & Innovation (BSBI)",
    ],
    visaPath: [
      { step: "Profile evaluation",   desc: "Check eligibility, course-fit, intake planning." },
      { step: "University application", desc: "Shortlist + apply to 4–6 best-fit universities." },
      { step: "Blocked account + APS", desc: "Help with €11,208 blocked account and APS where needed." },
      { step: "Visa filing",          desc: "Document checklist, embassy appointment, mock interviews." },
      { step: "Pre-departure",        desc: "Tickets, SIM, accommodation, registration in Berlin." },
    ],
    cost: [
      { item: "Tuition (public)",      val: "€0 – €1,500 / yr" },
      { item: "Tuition (private)",     val: "€8,000 – €18,000 / yr" },
      { item: "Living expenses",       val: "€934 / month (blocked account)" },
      { item: "Health insurance",      val: "€120 / month" },
      { item: "Visa application fee",  val: "€75" },
    ],
    intakes: ["Winter (Sep / Oct)", "Summer (Mar / Apr)"],
    faqs: [
      { q: "Is IELTS mandatory for Germany?", a: "Most universities require IELTS 6.5 or equivalent for English-taught programs, but a few accept TOEFL or even MOI (Medium of Instruction). For German-taught programs you'll need TestDaF or DSH." },
      { q: "Can I work part-time in Germany?", a: "Yes — students can work 120 full days or 240 half days per year. Most students earn €450–€520 per month from part-time jobs." },
      { q: "How much blocked account is required?", a: "€11,208 per year is the standard requirement. You open the account before your visa interview." },
    ],
  },
  {
    slug: "canada",
    name: "Canada",
    flag: "🇨🇦",
    tagline: "PR-friendly pathways",
    accent: { bg: "bg-red-50", text: "text-red-700", ring: "ring-red-200" },
    hero: {
      headline: "Canada — the world's most PR-friendly study destination.",
      sub: "Quick SDS visas, post-graduation work permit, and an obvious route to permanent residence. We file 50+ Canadian visas every month.",
    },
    whyChoose: [
      { title: "SDS visa in 4–6 weeks",        desc: "Student Direct Stream is built for Indian students with a fast track." },
      { title: "Up to 3-yr post-graduation work", desc: "Stay and work in Canada after your studies for up to 3 years." },
      { title: "Clear PR pathway",             desc: "Express Entry, PNP, CEC — Canada actively invites educated graduates." },
      { title: "Affordable colleges",          desc: "Public colleges offer one-year and two-year diplomas with great ROI." },
      { title: "Spouse open work permit",      desc: "Married students can take a spouse who gets an open work permit." },
      { title: "Welcoming, multicultural",      desc: "1.4M+ Indians already in Canada — strong community everywhere." },
    ],
    topUniversities: [
      "University of Toronto",
      "University of British Columbia",
      "McMaster University",
      "Conestoga College",
      "Humber College",
      "Centennial College",
    ],
    visaPath: [
      { step: "Course & college fit",          desc: "Pick a PGWP-eligible college based on your background." },
      { step: "Application + LOA",             desc: "Apply, secure a Letter of Acceptance." },
      { step: "GIC + tuition deposit",         desc: "Buy a Guaranteed Investment Certificate (CAD 20,635)." },
      { step: "SDS visa filing",               desc: "Biometrics, medical, document check, IRCC submission." },
      { step: "Departure",                      desc: "Flights, forex, SIM, accommodation, airport pickup." },
    ],
    cost: [
      { item: "Tuition (colleges)",     val: "CAD 14,000 – 20,000 / yr" },
      { item: "Tuition (universities)", val: "CAD 25,000 – 45,000 / yr" },
      { item: "GIC",                    val: "CAD 20,635" },
      { item: "Living expenses",        val: "CAD 12,000 – 15,000 / yr" },
      { item: "Visa fee",               val: "CAD 150" },
    ],
    intakes: ["Fall (Sep)", "Winter (Jan)", "Spring (May)"],
    faqs: [
      { q: "What is SDS visa?", a: "Student Direct Stream is the fastest visa route to Canada for students from select countries including India. Processing typically takes 4–6 weeks." },
      { q: "Can I work during studies?", a: "Yes — 20 hours / week off-campus during semesters and full-time during scheduled breaks." },
      { q: "How does the PR pathway work?", a: "After a 1–2 year program, students get a 1–3 year work permit, gain Canadian work experience, and can apply for PR through Express Entry, PNP or CEC." },
    ],
  },
  {
    slug: "australia",
    name: "Australia",
    flag: "🇦🇺",
    tagline: "Group of 8 ready",
    accent: { bg: "bg-yellow-50", text: "text-yellow-700", ring: "ring-yellow-200" },
    hero: {
      headline: "Australia — sun-drenched campuses, world-class research.",
      sub: "Group of 8 universities, post-study work up to 4 years and a generous PR points system. GTE-expert documentation is our specialty.",
    },
    whyChoose: [
      { title: "Group of 8 universities",  desc: "8 of the world's top 100 unis, all reachable from VisaXpert." },
      { title: "2–4 year post-study work", desc: "Work up to 4 years after completing a master's." },
      { title: "Spouse work rights",       desc: "Spouse of a master's student gets unrestricted work rights." },
      { title: "PR-friendly skills list",  desc: "STEM and healthcare grads have a clear PR pathway." },
      { title: "GTE expertise",            desc: "Genuine Temporary Entrant statements are our differentiator." },
      { title: "World-class research",     desc: "Massive funding for postgrad research." },
    ],
    topUniversities: [
      "University of Melbourne",
      "Australian National University",
      "University of Sydney",
      "University of Queensland",
      "Monash University",
      "RMIT University",
    ],
    visaPath: [
      { step: "Course fit + GTE plan",  desc: "Align course choice with intent and finances." },
      { step: "University offer",        desc: "Conditional or unconditional offer." },
      { step: "CoE (Confirmation of Enrolment)", desc: "Issued after first-semester tuition is paid." },
      { step: "Visa filing (subclass 500)", desc: "Health insurance (OSHC), biometrics, GTE letter." },
      { step: "Departure",                desc: "Forex, SIM, housing." },
    ],
    cost: [
      { item: "Tuition (UG)",          val: "AUD 25,000 – 40,000 / yr" },
      { item: "Tuition (PG)",          val: "AUD 30,000 – 55,000 / yr" },
      { item: "Living expenses",       val: "AUD 24,505 / yr" },
      { item: "OSHC (health cover)",   val: "AUD 600 – 900 / yr" },
      { item: "Visa fee",              val: "AUD 1,600" },
    ],
    intakes: ["February", "July", "November (limited)"],
    faqs: [
      { q: "How important is the GTE statement?", a: "Hugely. Australia's GTE assessment is strict — VisaXpert specialises in crafting compelling, document-backed statements." },
      { q: "Do I need health insurance?", a: "Yes, OSHC (Overseas Student Health Cover) is mandatory for the entire visa duration." },
    ],
  },
  {
    slug: "ireland",
    name: "Ireland",
    flag: "🇮🇪",
    tagline: "Two-year stay-back",
    accent: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200" },
    hero: {
      headline: "Ireland — Europe's English-speaking tech hub.",
      sub: "Two-year post-study visa, English-speaking, and home to Google, Meta, LinkedIn and Pfizer's European HQs. Quietly the smartest EU pick.",
    },
    whyChoose: [
      { title: "2-year post-study visa", desc: "Stay and work for 2 years after a master's, 1 year after a bachelor's." },
      { title: "English-speaking EU",    desc: "Only major English-speaking EU country left after Brexit." },
      { title: "Big-tech HQs in Dublin", desc: "Google, Meta, LinkedIn, Apple, Pfizer — all have European HQs here." },
      { title: "World top 100 unis",     desc: "Trinity, UCD are global top 100." },
      { title: "Welcoming visa policy",  desc: "Spouse can join after enrolment with work rights." },
      { title: "Clear PR / citizenship", desc: "5 years of legal residence makes you eligible for citizenship." },
    ],
    topUniversities: [
      "Trinity College Dublin",
      "University College Dublin",
      "University College Cork",
      "National University of Ireland Galway",
      "Dublin City University",
      "Maynooth University",
    ],
    visaPath: [
      { step: "Programme & offer",       desc: "Apply to 4–5 unis based on subject + budget." },
      { step: "Letter of acceptance",    desc: "Pay tuition deposit (€6,000+)." },
      { step: "Bank statement & docs",   desc: "Show €10,000 living expense funds." },
      { step: "Visa filing (Type D)",    desc: "Online application + biometrics + interview." },
      { step: "Departure",                desc: "Accommodation in Dublin/Cork/Limerick + GNIB registration." },
    ],
    cost: [
      { item: "Tuition",            val: "€10,000 – 25,000 / yr" },
      { item: "Living expenses",    val: "€10,000 / yr" },
      { item: "Health insurance",   val: "€500 / yr" },
      { item: "Visa fee",           val: "€60" },
    ],
    intakes: ["September", "January"],
    faqs: [
      { q: "Why Ireland over the UK?", a: "Cheaper tuition, generous 2-year post-study visa (vs UK's 2 years now restricted), and an EU degree." },
    ],
  },
  {
    slug: "singapore",
    name: "Singapore",
    flag: "🇸🇬",
    tagline: "Asia's education hub",
    accent: { bg: "bg-pink-50", text: "text-pink-700", ring: "ring-pink-200" },
    hero: {
      headline: "Singapore — Asia's premier global education hub.",
      sub: "NUS, NTU and an ultra-safe, ultra-clean city-state with high salaries and a 6-month stay-back. Smart pick for STEM and finance.",
    },
    whyChoose: [
      { title: "Top Asian universities",   desc: "NUS and NTU are global top 20 — best in Asia." },
      { title: "High starting salaries",    desc: "Average grad starts at SGD 60,000+ per year." },
      { title: "Safe & multicultural",      desc: "Lowest crime rate in the world, 25% Indian-origin population." },
      { title: "Six-month stay-back",       desc: "Stay after graduation to job-hunt." },
      { title: "Short flight from India",   desc: "Only 4–5 hours from major Indian cities." },
      { title: "Strong scholarship pool",   desc: "ASEAN scholarships and merit-based tuition grants." },
    ],
    topUniversities: [
      "National University of Singapore (NUS)",
      "Nanyang Technological University (NTU)",
      "Singapore Management University (SMU)",
      "James Cook University Singapore",
      "Curtin Singapore",
      "PSB Academy",
    ],
    visaPath: [
      { step: "University application",  desc: "Apply 8–12 months ahead for public unis." },
      { step: "IPA (in-principle approval)", desc: "Issued before you fly." },
      { step: "Student Pass collection", desc: "Convert IPA to STP on arrival." },
      { step: "Medical & e-FIN",         desc: "Health check + biometrics in Singapore." },
    ],
    cost: [
      { item: "Tuition (public)",   val: "SGD 17,000 – 25,000 / yr" },
      { item: "Tuition (private)",  val: "SGD 12,000 – 18,000 / yr" },
      { item: "Living expenses",    val: "SGD 12,000 – 18,000 / yr" },
      { item: "Visa fee",           val: "SGD 90" },
    ],
    intakes: ["August", "January", "May (some)"],
    faqs: [
      { q: "Is part-time work allowed?", a: "Only at public universities — and limited to 16 hours/week during term." },
    ],
  },
  {
    slug: "dubai",
    name: "Dubai (UAE)",
    flag: "🇦🇪",
    tagline: "Tax-free career launchpad",
    accent: { bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200" },
    hero: {
      headline: "Dubai — your career, tax-free, two hours from home.",
      sub: "Global university branch campuses, zero-tax salaries and a fast-track post-study residence visa. The smartest mix of value and proximity.",
    },
    whyChoose: [
      { title: "Tax-free salaries",        desc: "Take home 100% of what you earn — no income tax in the UAE." },
      { title: "Branch campuses of world top unis", desc: "Manipal Dubai, Birla Institute, Murdoch, Heriot-Watt." },
      { title: "Indian-friendly culture",   desc: "Largest expat community in the UAE is Indian." },
      { title: "Post-study green visa",     desc: "Two-year residency after graduation to job-hunt." },
      { title: "Booming job market",         desc: "Tourism, finance, tech, real-estate, healthcare — all hiring." },
      { title: "2-hour flight from India",   desc: "Closer than most metro cities — weekends back home." },
    ],
    topUniversities: [
      "University of Birmingham Dubai",
      "Heriot-Watt University Dubai",
      "Murdoch University Dubai",
      "BITS Pilani Dubai",
      "Manipal Academy Dubai",
      "Curtin University Dubai",
    ],
    visaPath: [
      { step: "Programme selection",  desc: "Pick an accredited UAE campus + course." },
      { step: "Conditional offer",     desc: "Apply, get LOA, pay tuition deposit." },
      { step: "Student visa",           desc: "Sponsored by the university, processed in 2–4 weeks." },
      { step: "Emirates ID + medical",  desc: "On-arrival paperwork in Dubai." },
    ],
    cost: [
      { item: "Tuition",        val: "AED 35,000 – 65,000 / yr" },
      { item: "Living expenses", val: "AED 30,000 – 50,000 / yr" },
      { item: "Visa + medical",  val: "AED 3,000" },
    ],
    intakes: ["September", "January", "May"],
    faqs: [
      { q: "Can I work during studies?", a: "Yes, on a part-time work permit (up to 15 hours/week)." },
    ],
  },
  {
    slug: "finland",
    name: "Finland",
    flag: "🇫🇮",
    tagline: "Tuition-friendly EU",
    accent: { bg: "bg-sky-50", text: "text-sky-700", ring: "ring-sky-200" },
    hero: {
      headline: "Finland — the world's happiest country invites you to study.",
      sub: "World-class education, generous scholarships and a Schengen path to all of Europe. Tech, design and sustainability lead the way.",
    },
    whyChoose: [
      { title: "Up to 100% scholarship",   desc: "Most universities cover 50–100% tuition for top applicants." },
      { title: "World-class research",     desc: "Strong in engineering, computer science, design and education." },
      { title: "EU-recognised degrees",    desc: "Degrees valid across the entire Schengen area." },
      { title: "Stay-back permit",          desc: "Two-year residence permit after graduation to find work." },
      { title: "Quality of life",           desc: "Happiest country on Earth (UN World Happiness Report)." },
      { title: "English-taught programs",   desc: "1,000+ programs taught entirely in English." },
    ],
    topUniversities: [
      "University of Helsinki",
      "Aalto University",
      "University of Turku",
      "Tampere University",
      "LUT University",
      "Hanken School of Economics",
    ],
    visaPath: [
      { step: "Course selection",        desc: "Apply to up to 6 programs in a single round." },
      { step: "Entrance exam / interview", desc: "Required for most Finnish programs." },
      { step: "Letter of admission",      desc: "Receive offer + pay first-year tuition." },
      { step: "Residence permit",         desc: "Apply at the Finnish embassy with bank proof of €560/month." },
    ],
    cost: [
      { item: "Tuition",          val: "€8,000 – €18,000 / yr (often scholarship-covered)" },
      { item: "Living expenses",  val: "€6,720 / yr" },
      { item: "Residence permit fee", val: "€450" },
    ],
    intakes: ["September", "January"],
    faqs: [
      { q: "Are degrees in English?", a: "Yes, hundreds of bachelor's and master's are 100% English." },
    ],
  },
  {
    slug: "denmark",
    name: "Denmark",
    flag: "🇩🇰",
    tagline: "Hidden gem of Europe",
    accent: { bg: "bg-indigo-50", text: "text-indigo-700", ring: "ring-indigo-200" },
    hero: {
      headline: "Denmark — design, innovation, work-life balance.",
      sub: "World-class research universities, strong scholarships and a post-study residency permit. Quietly one of the EU's best-kept study secrets.",
    },
    whyChoose: [
      { title: "Top-ranked universities",      desc: "Copenhagen, Aarhus, DTU — all in QS top 200." },
      { title: "English-taught programs",      desc: "600+ programs entirely in English." },
      { title: "Generous scholarships",         desc: "Danish State + Erasmus scholarships available." },
      { title: "High wages, short hours",       desc: "Strong work-life balance with great pay." },
      { title: "Post-study residency",          desc: "Job-search permit to find work after graduation." },
      { title: "Bicycle-first lifestyle",       desc: "Affordable, sustainable cities — Copenhagen is iconic." },
    ],
    topUniversities: [
      "University of Copenhagen",
      "Aarhus University",
      "Technical University of Denmark",
      "Copenhagen Business School",
      "Aalborg University",
      "University of Southern Denmark",
    ],
    visaPath: [
      { step: "University application",   desc: "Apply through optagelse.dk or direct portals." },
      { step: "Offer + tuition deposit",   desc: "Tuition fee guarantees a place." },
      { step: "Residence permit",          desc: "Apply with bank proof of DKK 6,397/month." },
    ],
    cost: [
      { item: "Tuition",        val: "DKK 45,000 – 120,000 / yr" },
      { item: "Living expenses", val: "DKK 6,397 / month" },
      { item: "Residence permit", val: "DKK 2,225" },
    ],
    intakes: ["September", "February"],
    faqs: [
      { q: "Is Danish required?", a: "Not for English-taught programs, but basic Danish helps with jobs." },
    ],
  },
];

export const COUNTRY_BY_SLUG = Object.fromEntries(COUNTRIES.map((c) => [c.slug, c]));
